import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Title, Text, TextInput, Button } from '@tremor/react';
import { api } from '../../lib/api';

interface NewBundleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (key: string) => void;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function NewBundleModal({ isOpen, onClose, onSuccess }: NewBundleModalProps) {
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setKey('');
      setKeyTouched(false);
      setName('');
      setDescription('');
      setVisibility('public');
      setError(null);
    }
  }, [isOpen]);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!keyTouched) {
      setKey(slugify(value));
    }
  };

  const handleKeyChange = (value: string) => {
    setKey(value);
    setKeyTouched(true);
  };

  const handleSubmit = async () => {
    if (!key.trim() || !name.trim()) {
      setError('Key and name are required');
      return;
    }

    if (!/^[a-zA-Z][a-zA-Z0-9-]*$/.test(key)) {
      setError('Key must start with a letter and contain only letters, numbers, and hyphens');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const trimmedKey = key.trim();
      await api.createBundle({
        key: trimmedKey,
        name: name.trim(),
        description: description.trim() || undefined,
        classifiers: [],
        visibility,
      });
      onSuccess(trimmedKey);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create bundle');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} static={true}>
      <DialogPanel className="bg-gray-900 border border-gray-800 max-w-md">
        <Title className="text-white">New Classifier Bundle</Title>
        <Text className="text-gray-400 mt-1">
          Create a bundle to define which classifiers and labels to use for a specific use case. You'll configure classifiers after creation.
        </Text>

        <div className="mt-4 space-y-4">
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Name</label>
            <TextInput
              placeholder="e.g., iConect Default, Outlook Reflect"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Key</label>
            <TextInput
              placeholder="e.g., iconect-default, outlook-reflect"
              value={key}
              onChange={(e) => handleKeyChange(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Stable identifier. Cannot be changed later.</p>
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Visibility</label>
            <div className="mt-1 inline-flex rounded border border-gray-700 bg-gray-800 p-0.5 gap-0.5">
              <button
                type="button"
                onClick={() => setVisibility('public')}
                className={`px-3 py-1.5 text-sm rounded ${visibility === 'public' ? 'bg-blue-900/60 text-blue-100' : 'text-gray-400 hover:text-gray-200'}`}
              >
                Public
              </button>
              <button
                type="button"
                onClick={() => setVisibility('private')}
                className={`px-3 py-1.5 text-sm rounded ${visibility === 'private' ? 'bg-blue-900/60 text-blue-100' : 'text-gray-400 hover:text-gray-200'}`}
              >
                Private
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Public bundles are visible to public-only orgs and personal keys. Private ones are reachable only via an org allowlist grant, a denylist org, or an admin.
            </p>
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Description (optional)</label>
            <TextInput
              placeholder="What this bundle is used for"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1"
            />
          </div>

          {error && (
            <div className="bg-red-950/50 border border-red-900 rounded p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isLoading} disabled={!key.trim() || !name.trim()}>
            Create Bundle
          </Button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
